/**
 * Fabrique des blocs CMS avancé de la categorie "member" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { MemberFavoritesPreview, MemberLoginPreview, MemberProfilePreview, MemberSignupPreview } from "@/components/cms-puck/previews/MemberFlowPreviews";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { type RawComponentsConfig } from "../types";

export function buildMemberBlocks() {
  return {
    MemberLogin: {
      label: cmsNew("cms.new.blocks.memberLogin.label", "Connexion"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.parcours_membre",
            "Parcours membre",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.memberLogin.pre",
                "Assignez cette page comme modèle « connexion » dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/parcours-membre", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.memberFlow", "Parcours membre")}
              </button>
              {cmsNew(
                "cms.new.hint.memberFlow.designHint",
                ". Personnalisez la typo, les couleurs et le traitement du texte via l'onglet Design.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: ({ ...rest }) => (
        <MemberLoginPreview
          style={(rest as { style?: BlockStyle }).style ?? undefined}
        />
      ),
    },

    MemberSignup: {
      label: cmsNew("cms.new.blocks.memberSignup.label", "Inscription"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.parcours_membre",
            "Parcours membre",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.memberSignup.pre",
                "Assignez cette page comme modèle « inscription » dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/parcours-membre", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.memberFlow", "Parcours membre")}
              </button>
              {cmsNew(
                "cms.new.hint.memberFlow.designHint",
                ". Personnalisez la typo, les couleurs et le traitement du texte via l'onglet Design.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: ({ ...rest }) => (
        <MemberSignupPreview
          style={(rest as { style?: BlockStyle }).style ?? undefined}
        />
      ),
    },

    MemberProfile: {
      label: cmsNew("cms.new.blocks.memberProfile.label", "Profil"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.parcours_membre",
            "Parcours membre",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.memberProfile.pre",
                "Assignez cette page comme modèle « profil » dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/parcours-membre", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.memberFlow", "Parcours membre")}
              </button>
              {cmsNew(
                "cms.new.hint.memberFlow.addTitleHint",
                ". Ajoutez un titre via un bloc Titre au-dessus si besoin.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: ({ ...rest }) => (
        <MemberProfilePreview
          style={(rest as { style?: BlockStyle }).style ?? undefined}
        />
      ),
    },

    MemberFavorites: {
      label: cmsNew("cms.new.blocks.memberFavorites.label", "Favoris"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.parcours_membre",
            "Parcours membre",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.memberFavorites.pre",
                "Assignez cette page comme modèle « favoris » dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/parcours-membre", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.memberFlow", "Parcours membre")}
              </button>
              {cmsNew(
                "cms.new.hint.memberFlow.addTitleHint",
                ". Ajoutez un titre via un bloc Titre au-dessus si besoin.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: ({ ...rest }) => (
        <MemberFavoritesPreview
          style={(rest as { style?: BlockStyle }).style ?? undefined}
        />
      ),
    },
  } satisfies Pick<RawComponentsConfig, "MemberLogin" | "MemberSignup" | "MemberProfile" | "MemberFavorites">;
}
